import { RefObject } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Plus, Pencil } from "lucide-react";
import { formatMetricNumber } from "@/utils/keywordMetricDisplay";
import type { Keyword } from "@/types/keyword";

interface ThemeNameAutocompleteProps {
  name: string;
  onNameChange: (value: string) => void;
  showDropdown: boolean;
  onShowDropdownChange: (show: boolean) => void;
  nameSuggestions: Keyword[];
  stagedName: string | null;
  onStage: (value?: string, keywordId?: number) => void;
  onUnstage: () => void;
  nameInputRef: RefObject<HTMLInputElement>;
}

export function ThemeNameAutocomplete({
  name,
  onNameChange,
  showDropdown,
  onShowDropdownChange,
  nameSuggestions,
  stagedName,
  onStage,
  onUnstage,
  nameInputRef,
}: ThemeNameAutocompleteProps) {
  return (
    <div className="space-y-2">
      <h3 className="font-semibold text-base">Add theme</h3>
      <p className="text-sm text-muted-foreground">
        Search your keyword bank or type a name for the new theme.
      </p>

      {stagedName ? (
        /* Name locked — show it with an edit button */
        <div className="flex items-center gap-2 py-1">
          <span className="font-semibold text-base">{stagedName}</span>
          <button
            onClick={onUnstage}
            className="text-muted-foreground hover:text-foreground"
            title="Edit theme name"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        /* Name input with autocomplete */
        <>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                ref={nameInputRef}
                autoFocus
                value={name}
                onChange={(e) => { onNameChange(e.target.value); onShowDropdownChange(true); }}
                onFocus={() => onShowDropdownChange(true)}
                onBlur={() => setTimeout(() => onShowDropdownChange(false), 150)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") onStage();
                  if (e.key === "Escape") onShowDropdownChange(false);
                }}
                placeholder="e.g. Waterproof & Sandproof"
                className="pl-8"
              />
            </div>
            <Button onClick={() => onStage()} disabled={!name.trim()}>
              <Plus className="h-4 w-4" />
              Add theme
            </Button>
          </div>

          {showDropdown && nameSuggestions.length > 0 && (
            <div className="border rounded-md bg-popover shadow-md overflow-hidden">
              {nameSuggestions.map((kw) => (
                <button
                  key={kw.id}
                  type="button"
                  onMouseDown={() => onStage(kw.search, kw.id)}
                  className="w-full flex items-center justify-between px-3 py-2.5 text-sm hover:bg-muted/60 transition-colors border-b border-border/40 last:border-b-0"
                >
                  <span>{kw.search}</span>
                  {kw.search_volume != null && (
                    <span className="text-xs text-muted-foreground shrink-0 ml-4">
                      {formatMetricNumber(kw.search_volume)} vol/mo
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
